import type { VNode } from "preact";
import { SoundName } from "../../../resources/resources";
import { Game } from "../Game";
import { ReactEntity } from "../ReactEntity";
import { BaseEntity } from "../entity/BaseEntity";
import Entity from "../entity/Entity";
import { on } from "../entity/handler";
import { registerManifestFonts } from "./fonts";
import { getBiggestSounds, getTotalSoundBytes, loadSound } from "./sounds";

export type ResourceManifest = {
  images: { [name: string]: string };
  sounds: { [name: string]: string };
  fonts: { [name: string]: string };
};

interface RenderInfo {
  fonts: { loaded: number; total: number };
  images: { loaded: number; total: number };
  sounds: { loaded: number; total: number };
}

/**
 * A React-enabled asset preloader that loads images, sounds, and fonts
 * with progress tracking. Provides real-time loading feedback through
 * React UI components and resolves when all assets are ready.
 */
export class ReactPreloader extends BaseEntity implements Entity {
  private _resolve!: () => void;
  private _promise!: Promise<void>;

  private progress: RenderInfo = {
    fonts: {
      loaded: 0,
      total: 0,
    },
    images: {
      loaded: 0,
      total: 0,
    },
    sounds: {
      loaded: 0,
      total: 0,
    },
  };

  constructor(
    private manifest: ResourceManifest,
    reactRender: (props: RenderInfo) => VNode,
  ) {
    super();

    this._promise = new Promise((resolve) => {
      this._resolve = resolve;
    });

    this.addChild(new ReactEntity(() => reactRender(this.progress)));
  }

  @on("add")
  async onAdd({ game }: { game: Game }) {
    await Promise.all([
      this.loadFonts(),
      this.loadSounds(game.audio),
      this.loadImages(game),
    ]);
    const bytes = getTotalSoundBytes();

    console.groupCollapsed(
      `Audio Loaded: ${(bytes / 2 ** 20).toFixed(1)}MB total`,
    );

    getBiggestSounds()
      .slice(0, 5)
      .forEach(([url, size]) =>
        console.info(url, "\n", `${(size / 1024).toFixed(1)}kB`),
      );

    console.groupEnd();
    this._resolve();
  }

  waitTillLoaded() {
    return this._promise;
  }

  async loadFonts() {
    this.progress.fonts.total = Object.values(this.manifest.fonts).length;
    this.progress.fonts.loaded = 0;

    try {
      await registerManifestFonts(this.manifest.fonts, () => {
        this.progress.fonts.loaded += 1;
      });
    } catch (e) {
      console.error("Fonts failed to load", e);
    }
  }

  async loadSounds(audioContext: AudioContext) {
    this.progress.sounds.loaded = 0;
    this.progress.sounds.total = Object.values(this.manifest.sounds).length;

    await Promise.all(
      Object.entries(this.manifest.sounds).map(async ([name, url]) => {
        try {
          await loadSound(name as SoundName, url, audioContext);
        } catch (e) {
          console.warn(`Sound failed to load: ${url}, ${url}`, e);
        }
        this.progress.sounds.loaded += 1;
      }),
    );
  }

  async loadImages(game: Game) {
    this.progress.images.loaded = 0;
    this.progress.images.total = Object.values(this.manifest.images).length;

    const textureManager = game.getRenderer().textureManager;

    await Promise.all(
      Object.entries(this.manifest.images).map(async ([name, url]) => {
        try {
          await textureManager.load(url);
        } catch (e) {
          console.warn(`Image failed to load: ${url}`, e);
        }
        this.progress.images.loaded += 1;
      }),
    );
  }

  @on("destroy")
  onDestroy() {
    document.getElementById("preloader")?.remove();
  }
}
